import React, { useState, useEffect } from 'react';
import { FaMapMarkerAlt, FaArrowRight, FaSearch, FaGlobe, FaStar } from 'react-icons/fa';
import SEO from './SEO';

const API_BASE = process.env.REACT_APP_API_URL || '';

// Popular destinations with images
const FEATURED_DESTINATIONS = [
  { name: 'Paris', country: 'France', region: 'Europe', image: 'https://images.unsplash.com/photo-1502602898657-3e91760cbb34?w=400&h=300&fit=crop' },
  { name: 'Tokyo', country: 'Japan', region: 'Asia', image: 'https://images.unsplash.com/photo-1540959733332-eab4deabeeaf?w=400&h=300&fit=crop' },
  { name: 'New York City', country: 'USA', region: 'North America', image: 'https://images.unsplash.com/photo-1496442226666-8d4d0e62e6e9?w=400&h=300&fit=crop' },
  { name: 'Barcelona', country: 'Spain', region: 'Europe', image: 'https://images.unsplash.com/photo-1583422409516-2895a77efded?w=400&h=300&fit=crop' },
  { name: 'Bali', country: 'Indonesia', region: 'Asia', image: 'https://images.unsplash.com/photo-1537996194471-e657df975ab4?w=400&h=300&fit=crop' },
  { name: 'Rome', country: 'Italy', region: 'Europe', image: 'https://images.unsplash.com/photo-1552832230-c0197dd311b5?w=400&h=300&fit=crop' },
  { name: 'London', country: 'UK', region: 'Europe', image: 'https://images.unsplash.com/photo-1513635269975-59663e0ac1ad?w=400&h=300&fit=crop' },
  { name: 'Sydney', country: 'Australia', region: 'Oceania', image: 'https://images.unsplash.com/photo-1506973035872-a4ec16b8e8d9?w=400&h=300&fit=crop' },
  { name: 'Dubai', country: 'UAE', region: 'Middle East', image: 'https://images.unsplash.com/photo-1512453979798-5ea266f8880c?w=400&h=300&fit=crop' },
  { name: 'Bangkok', country: 'Thailand', region: 'Asia', image: 'https://images.unsplash.com/photo-1508009603885-50cf7c579365?w=400&h=300&fit=crop' },
  { name: 'Amsterdam', country: 'Netherlands', region: 'Europe', image: 'https://images.unsplash.com/photo-1534351590666-13e3e96b5017?w=400&h=300&fit=crop' },
  { name: 'Santorini', country: 'Greece', region: 'Europe', image: 'https://images.unsplash.com/photo-1570077188670-e3a8d69ac5ff?w=400&h=300&fit=crop' },
];

const REGIONS = [
  { name: 'Europe', emoji: '🇪🇺', count: 25 },
  { name: 'Asia', emoji: '🌏', count: 18 },
  { name: 'North America', emoji: '🌎', count: 12 },
  { name: 'South America', emoji: '🌎', count: 8 },
  { name: 'Africa', emoji: '🌍', count: 10 },
  { name: 'Oceania', emoji: '🌏', count: 5 },
  { name: 'Middle East', emoji: '🏜️', count: 6 },
];

function Destinations({ onBack, onViewArticle, onGetStarted }) {
  const [destinations, setDestinations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRegion, setSelectedRegion] = useState(null);

  useEffect(() => {
    fetchDestinations();
  }, []);

  const fetchDestinations = async () => {
    try {
      // Fetch unique destinations from articles
      const response = await fetch(`${API_BASE}/api/blog/destinations`);
      if (response.ok) {
        const data = await response.json();
        setDestinations(data.destinations || []);
      }
    } catch (error) {
      console.error('Error fetching destinations:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredDestinations = FEATURED_DESTINATIONS.filter((dest) => {
    const matchesSearch = dest.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         dest.country.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRegion = !selectedRegion || dest.region === selectedRegion;
    return matchesSearch && matchesRegion;
  });

  const handleDestinationClick = (destination) => {
    // Navigate to blog with destination filter
    window.location.href = `/blog?destination=${encodeURIComponent(destination)}`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-neutral-50 to-white">
      <SEO
        title="Travel Destinations | ItinerarySeek"
        description="Explore our travel guides for destinations worldwide. Find tips, itineraries, and local insights for your next adventure."
        canonical="https://itineraryseek.com/destinations"
      />

      {/* Hero Section */}
      <div className="relative bg-gradient-to-r from-primary-600 to-secondary-600 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <FaGlobe className="absolute -right-20 -top-20 text-[400px]" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          {onBack && (
            <button
              onClick={onBack}
              className="mb-4 text-white/80 hover:text-white flex items-center gap-2"
            >
              <span>&larr;</span> Back
            </button>
          )}
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Explore Destinations</h1>
          <p className="text-xl text-white/90 max-w-2xl mb-8">
            Discover travel guides, tips, and insider knowledge for destinations around the world.
          </p>

          {/* Search Bar */}
          <div className="max-w-xl">
            <div className="relative">
              <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400" />
              <input
                type="text"
                placeholder="Search destinations..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-3 rounded-xl text-neutral-800 shadow-lg focus:outline-none focus:ring-2 focus:ring-white/50"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Region Filter */}
        <section className="mb-12">
          <h2 className="text-lg font-semibold text-neutral-700 mb-4">Browse by Region</h2>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => setSelectedRegion(null)}
              className={`px-4 py-2 rounded-full font-medium transition-colors ${
                !selectedRegion
                  ? 'bg-primary-600 text-white'
                  : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'
              }`}
            >
              All Regions
            </button>
            {REGIONS.map((region) => (
              <button
                key={region.name}
                onClick={() => setSelectedRegion(region.name)}
                className={`px-4 py-2 rounded-full font-medium transition-colors flex items-center gap-2 ${
                  selectedRegion === region.name
                    ? 'bg-primary-600 text-white'
                    : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'
                }`}
              >
                <span>{region.emoji}</span>
                {region.name}
              </button>
            ))}
          </div>
        </section>

        {/* Featured Destinations Grid */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold text-neutral-800 mb-6">
            {selectedRegion ? `Destinations in ${selectedRegion}` : 'Popular Destinations'}
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredDestinations.map((dest) => (
              <div
                key={dest.name}
                onClick={() => handleDestinationClick(dest.name)}
                className="group cursor-pointer"
              >
                <div className="relative rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-shadow">
                  <img
                    src={dest.image}
                    alt={dest.name}
                    className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <h3 className="text-xl font-bold text-white">{dest.name}</h3>
                    <p className="text-white/80 text-sm flex items-center gap-1">
                      <FaMapMarkerAlt className="text-xs" />
                      {dest.country}
                    </p>
                  </div>
                  <div className="absolute top-3 right-3 bg-white/90 backdrop-blur-sm px-2 py-1 rounded-full text-xs font-medium text-neutral-700">
                    {dest.region}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {filteredDestinations.length === 0 && (
            <div className="text-center py-12 text-neutral-500">
              <FaSearch className="text-4xl mx-auto mb-4 opacity-50" />
              <p>No destinations found matching your search.</p>
            </div>
          )}
        </section>

        {/* Destinations from Blog */}
        {destinations.length > 0 && (
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-neutral-800 mb-6">All Covered Destinations</h2>
            <div className="flex flex-wrap gap-2">
              {destinations.map((dest) => (
                <button
                  key={dest.name}
                  onClick={() => handleDestinationClick(dest.name)}
                  className="px-4 py-2 bg-white border border-neutral-200 rounded-lg hover:border-primary-300 hover:bg-primary-50 transition-colors text-sm font-medium text-neutral-700 flex items-center gap-2"
                >
                  <FaMapMarkerAlt className="text-primary-500 text-xs" />
                  {dest.name}
                  {dest.article_count && (
                    <span className="text-xs text-neutral-400">({dest.article_count})</span>
                  )}
                </button>
              ))}
            </div>
          </section>
        )}

        {/* Coming Soon */}
        <section className="bg-neutral-100 rounded-2xl p-8 mb-12">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h2 className="text-xl font-bold text-neutral-800 mb-2">
                More Destinations Coming Soon
              </h2>
              <p className="text-neutral-600">
                We're constantly adding new destination guides. Check back often or sign up to get notified.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <FaStar className="text-yellow-500" />
              <span className="font-semibold text-neutral-700">100+ guides in progress</span>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="text-center py-12 border-t border-neutral-200">
          <h2 className="text-2xl font-bold text-neutral-800 mb-4">
            Ready to Start Planning?
          </h2>
          <p className="text-neutral-600 mb-6 max-w-xl mx-auto">
            Let our AI create a personalized itinerary for any destination in minutes.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            {onGetStarted ? (
              <button
                onClick={onGetStarted}
                className="bg-primary-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-primary-700 transition-colors"
              >
                Create Free Itinerary
              </button>
            ) : (
              <a
                href="/register"
                className="inline-block bg-primary-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-primary-700 transition-colors"
              >
                Create Free Itinerary
              </a>
            )}
            <a
              href="/guides"
              className="inline-flex items-center justify-center gap-2 border-2 border-primary-600 text-primary-600 px-8 py-3 rounded-lg font-semibold hover:bg-primary-50 transition-colors"
            >
              Browse Travel Guides <FaArrowRight />
            </a>
          </div>
        </section>
      </div>
    </div>
  );
}

export default Destinations;
